/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). "Explain this forecast": how
 * the model reached the number for one series, written as markdown for the chat
 * assistant. The details page button and the chat suggestion both play it, so
 * the answer is the same either way. Every figure comes from the same engine
 * the page uses, so it stays true when a buyer overrides the forecast.
 */
import {
  DEFAULT_SERIES_ID,
  describeSpec,
  isFinishedGoodSku,
} from './forecast-data';
import { buildViews, explainView } from './forecast-engine';
import { fmtUnits } from './forecast-format';
import { sum } from './forecast-math';
import {
  neededSentence,
  partUseFor,
  usedInSentence,
} from './forecast-part-use';
import { getForecastState } from './forecast-store';
import type { ForecastView } from './forecast-types';

/** The suggestion chip, the button's message and the first wording that matches. */
export const EXPLAIN_FORECAST_PROMPT = 'Explain this forecast';

const PARTS_PLAN_LINK = '[parts plan](/procurement/forecast/bom)';

/** The other side of the item on its own page: a part's panels, or a panel's parts. */
function viewLink(seriesId: string, view: 'buy' | 'sell'): string {
  return `[what we ${view}](/procurement/forecast/details?series=${encodeURIComponent(seriesId)}&view=${view})`;
}

const EXPLAIN_PATTERNS: readonly RegExp[] = [
  /^(please )?explain (this|the|that) forecast\b/,
  /^(please )?walk me through (it|this|the forecast|this forecast)\b/,
  /^how (was|is|did) (this|the) forecast (calculated|made|built|reached|work)/,
  /^how did you (get|reach|calculate) (this|the) (forecast|number)/,
];

function plain(message: string): string {
  return message
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** True when a chat message asks for this explanation. */
export function isExplainForecastRequest(message: string): boolean {
  const text = plain(message);
  return EXPLAIN_PATTERNS.some((pattern) => pattern.test(text));
}

function sentence(text: string): string {
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

/** The explanation of one series, in markdown. */
export function explainNarration(
  view: ForecastView,
  views: readonly ForecastView[],
): string {
  const explanation = explainView(view);
  const { unit } = view.spec;
  const points = [...explanation.points];

  const use = partUseFor(view, views);
  if (use) {
    points.unshift(
      `We use this in ${usedInSentence(use)}. ${neededSentence(use, unit)} See ${viewLink(view.id, 'sell')} or the ${PARTS_PLAN_LINK}.`,
    );
  } else if (isFinishedGoodSku(view.spec.sku)) {
    const builds = sum(view.adjusted.slice(0, 6));
    points.unshift(
      `The ${PARTS_PLAN_LINK} builds ${fmtUnits(builds, unit)} over the next six months from this forecast, so a change here changes the parts it orders. See ${viewLink(view.id, 'buy')}.`,
    );
  }

  const steps = explanation.steps.map(
    (step, index) =>
      `${index + 1}. **${step.label}.** ${sentence(step.detail)}`,
  );
  return [
    `**${view.id}** (${describeSpec(view.spec)}) is forecast at **${explanation.headline}**. Here is how I got there.`,
    steps.join('\n'),
    `What stands out:\n\n${points.map((point) => `- ${point}`).join('\n')}`,
  ].join('\n\n');
}

/** The explanation for a series id, read from the current forecast state. */
export function explainNarrationFor(seriesId: string | null): string {
  const views = buildViews(getForecastState());
  const id = seriesId ?? DEFAULT_SERIES_ID;
  const view = views.find((candidate) => candidate.id === id);
  if (!view) {
    return 'Open an item on the forecast details page and I can explain how its forecast was reached.';
  }
  return explainNarration(view, views);
}
